import { AnimatedBarSeries } from '@visx/xychart';
import { Text } from '@visx/text';
import { memo, useCallback, useContext } from 'react';
import { DataContext } from '@visx/xychart';

import { MarkProps } from '../types';

const xAccessor = (d: any) => d.label;
const yAccessor = (d: any) => d.data;

type BarSeriesProps = {
    seriesKey: string;
    label: string;
    data: any[];
    colorAccessor: (key: string) => string;
};

const BarSeries = memo(({ seriesKey, label, data, colorAccessor }: BarSeriesProps) => {
    const { xScale, yScale } = useContext(DataContext);
    const seriesColorAccessor = useCallback(() => colorAccessor(label), [colorAccessor, label]);

    return (
        <g key={seriesKey}>
            <AnimatedBarSeries
                data={data}
                dataKey={label}
                xAccessor={xAccessor}
                yAccessor={yAccessor}
                colorAccessor={seriesColorAccessor}
            />
            {data.map((d: any, index: number) => {
                const barX = xScale?.(d.label);
                const barY = yScale?.(d.data);
                const barWidth = (xScale as any)?.bandwidth?.();

                if (typeof barX !== 'number' || typeof barY !== 'number' || typeof barWidth !== 'number') {
                    return null;
                }

                return (
                    <Text
                        key={`${seriesKey}-annotation-${index}`}
                        x={barX + barWidth / 2}
                        y={barY - 8}
                        textAnchor="middle"
                        verticalAnchor="end"
                        fontSize={11}
                        fill="#757575"
                        fontWeight="normal"
                    >
                        {d.data}
                    </Text>
                );
            })}
        </g>
    );
});

export default function Bar({ data, colorAccessor }: MarkProps) {
    return (
        <>
            {data.map(({ key, label, data: seriesData }: { key: string; label: string; data: any[] }) => (
                <BarSeries
                    key={key}
                    seriesKey={key}
                    label={label}
                    data={seriesData}
                    colorAccessor={colorAccessor}
                />
            ))}
        </>
    );
}
